import { useLayoutEffect, useRef, useState } from 'react';
import type { ScrollGutterSizeT } from '../types';
type PropsT = {
  gridContainer: HTMLDivElement | null;
};

// We are not using CSS: `scrollbar-gutter: stable;` here because it doesn't add
// gutter on vertical axis. It only works on horizontal (inline) axis.
export function useGetGridScrollGutterSize({
  gridContainer
}: PropsT): ScrollGutterSizeT {
  const [gutter, setGutter] = useState<ScrollGutterSizeT>({
    right: 0,
    bottom: 0
  });
  const gutterValueRef = useRef<ScrollGutterSizeT>(gutter);
  // eslint-disable-next-line react-hooks/refs
  gutterValueRef.current = gutter;
  useLayoutEffect(() => {
    if (!gridContainer) {
      return;
    }
    const compute = () => {
      // > 0 for non-overlay (always-visible) scrollbars, 0 for overlay
      const right = gridContainer.offsetWidth - gridContainer.clientWidth;
      const bottom = gridContainer.offsetHeight - gridContainer.clientHeight;
      if (gutterValueRef.current.right === right && gutterValueRef.current.bottom === bottom) {
        return;
      }
      setGutter({
        right,
        bottom
      });
    };
    compute(); // initial
    const ro = new ResizeObserver(compute); // size changes
    ro.observe(gridContainer);
    const mo = new MutationObserver(compute); // style/class toggles etc.
    mo.observe(gridContainer, {
      attributes: true,
      attributeFilter: ['style', 'class']
    });
    window.addEventListener('resize', compute); // viewport DPI/zoom changes

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, [gridContainer, gutterValueRef]);
  return gutter;
}